import React, { lazy, useEffect, useState, useRef } from 'react';
import './App.css';
import { useAuth, hasAuthParams } from 'react-oidc-context';
import { useScrollToTop } from './hooks/use-scroll-to-top';
import ThemeProvider from './theme';
import Router from './routes/sections';
import cloudApi from './app/api/cloudApi';
import { useDispatch, useSelector } from 'react-redux';
import { setAccounts, setAccount, setSites, updatePermissions, setAvailableActions, setAllowedReportKeys } from './app/appSlice';
import LogIn from './layouts/LogIn';
import SetAccount from './layouts/SetAccount';
import Footer from "./components/footer"
import { setupAxiosInterceptors } from './app/api/baseApi';
import { useLocation, useNavigate } from 'react-router-dom';
import store from './app/store';

export default (function App() {
  const dispatch = useDispatch();
  const auth = useAuth();
  const location = useLocation();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const { accounts, account, availableActions } = useSelector((state) => state.app);

  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const navigate = useNavigate();
  
  // Track if we've already handled the current switch-account navigation
  const switchHandledRef = useRef(false);
  
  // Handle the switch account navigation
  const isSwitchAccountRequest = location.state?.switchAccount;
  
  // Clear account when switching - only process once per navigation
  useEffect(() => {
    if (isSwitchAccountRequest && !switchHandledRef.current) {
      switchHandledRef.current = true;
      dispatch(setAccount({ account: null }));
      // Use navigate to properly clear the location state
      navigate('/', { replace: true, state: {} });
    }
  }, [isSwitchAccountRequest, dispatch, navigate]);

  // Reset the ref when we navigate away from switch account
  useEffect(() => {
    if (!isSwitchAccountRequest) {
      switchHandledRef.current = false;
    }
  }, [isSwitchAccountRequest]);

  // Determine if we need to show account selection
  // Show selector if: no account OR switch request is active (before account is cleared)
  const needSetAccount = !account || isSwitchAccountRequest;

  const handleUpdatePermissions = (newPermissionLevel) => {
    dispatch(updatePermissions(newPermissionLevel));
  };


  useEffect(() => {
    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin) {
      // auth.signinRedirect();
      // setHasTriedSignin(true);
    }
  }, [auth, hasTriedSignin]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      setLoadingAccounts(true);
      cloudApi.getAccounts(auth.user.access_token)
        .then((r) => {
          dispatch(setAccounts({ accounts: r.data }));
          if (r.data.length === 1) {
            dispatch(setAccount({ account: r.data[0] }));
            localStorage.setItem('selectedAccount', JSON.stringify(r.data[0]));
          } else {
            const savedAccount = localStorage.getItem('selectedAccount');
            if (savedAccount) {
              const parsedAccount = JSON.parse(savedAccount);
              // Verify if the saved account still exists in the available accounts
              const accountExists = r.data.find(acc => acc.id === parsedAccount.id);
              if (accountExists) {
                dispatch(setAccount({ account: parsedAccount }));
              } else {
                localStorage.removeItem('selectedAccount');
              }
            }
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setLoadingAccounts(false);
        });
    }
  }, [auth.isAuthenticated]);

  useEffect(() => {
    if (auth.isAuthenticated && account) {
      cloudApi.getAvailableActions(auth.user.access_token).then((r) => {
        dispatch(setAvailableActions(r.data));
      });
      cloudApi.getAllowedReportKeys(auth.user.access_token, account.id)
        .then((r) => {
          dispatch(setAllowedReportKeys(Array.isArray(r.data) ? r.data : []));
        })
        .catch((err) => {
          console.log(err);
          dispatch(setAllowedReportKeys([]));
        });
    }
    const fetchData = async () => {
      let ss = await cloudApi.getSitesMetadata(auth.user.access_token, account.id);
      dispatch(setSites({ sites: ss.data }));
    };
    if (account) {
      fetchData();
      const roles = JSON.parse(atob(auth.user.access_token.split('.')[1])).realm_access.roles;
      if(!roles.includes('admin') && !roles.includes('manager')) {
        handleUpdatePermissions("Client")
      }
    }
  }, [account, auth.user?.access_token, dispatch]);

  useScrollToTop();

  const handleSetAccount = (selectedAccount) => {
    dispatch(setAccount({ account: selectedAccount }));
    localStorage.setItem('selectedAccount', JSON.stringify(selectedAccount));
  };

  const handleCreateAccount = async (payload) => {
    const response = await cloudApi.createAccount(auth.user.access_token, payload);
    const refreshedAccounts = await cloudApi.getAccounts(auth.user.access_token);
    dispatch(setAccounts({ accounts: refreshedAccounts.data }));
    return response?.data;
  };
  
  const hasCreateAccountAction = availableActions.includes('EDIT_ACCOUNTS');
  const [canCreateAccount, setCanCreateAccount] = useState(false);

  useEffect(() => {
    if (hasCreateAccountAction) {
      setCanCreateAccount(true);
    }
  }, [hasCreateAccountAction]);

  useEffect(() => {
    // Set up the Axios interceptors with store instance
    setupAxiosInterceptors(auth.signinRedirect, store);
  }, [auth]);

  return (
    <ThemeProvider>
      {
        auth.isAuthenticated
          ?
            needSetAccount
            ? <SetAccount accounts={accounts} setAccount={handleSetAccount} isLoading={loadingAccounts} canCreateAccount={canCreateAccount} createAccount={handleCreateAccount} />
            : <Router />
          : <LogIn />
      }
      {
        auth.isAuthenticated
          ? <Footer />
          : null
      }
    </ThemeProvider>
  );

})

